import { type FC, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/api/supabase';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';

interface DayPassView {
  code: string;
  status: string;
  passes: Array<{ dayPassId: string; dayPassName: string; attendeeName: string; redeemedAt: string | null }>;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const POLL_ATTEMPTS = 6;

export const DayPassConfirmationPage: FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') ?? '';
  const [view, setView] = useState<DayPassView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!sessionId) { setLoading(false); return; }
      // Poll to cover the webhook race: Stripe redirects the buyer back before the
      // webhook has necessarily marked the order paid.
      for (let i = 0; i < POLL_ATTEMPTS; i++) {
        const { data } = await supabase.functions.invoke('daypass-get-order', { body: { sessionId } });
        if (cancelled) return;
        if (data && !data.error) {
          setView(data as DayPassView);
          if ((data as DayPassView).status === 'paid') break;
        }
        // Don't sleep after the final attempt — it only delays the fallback UI.
        if (i < POLL_ATTEMPTS - 1) await sleep(2000);
        if (cancelled) return;
      }
      if (!cancelled) setLoading(false);
    };
    void run();
    return () => { cancelled = true; };
  }, [sessionId]);

  const passList = view && (
    <ul className="divide-y divide-turquoise-500/40">
      {view.passes.map((p, i) => (
        <li key={`${p.dayPassId}-${i}`} className="py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-white font-semibold">{p.attendeeName}</p>
            <p className="text-orange-200 text-sm">{p.dayPassName}</p>
          </div>
          {p.redeemedAt && <span className="text-sm text-orange-300">Checked in</span>}
        </li>
      ))}
    </ul>
  );

  const orderCodeNote = view?.code ? (
    <p className="mt-6 text-center text-white/80 text-sm">
      If staff can&apos;t find you by name, show them order code{' '}
      <span className="font-mono font-semibold text-white">{view.code}</span>.
    </p>
  ) : null;

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="text-center text-white mb-8">
          <h1 className="text-4xl font-viking mb-2 drop-shadow-lg">Your Day Passes</h1>
          {view?.code && <p className="text-orange-200">Order {view.code}</p>}
        </div>

        {loading && !view && (
          <div className="text-center text-white">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-orange-500 border-r-transparent"></div>
            <p className="mt-4">Confirming your payment…</p>
          </div>
        )}

        {!loading && !view && (
          <Card><CardContent className="py-12 text-center">
            <p className="text-white/90 mb-4">We couldn&apos;t find that order. If you just paid, check the email we sent you.</p>
            {!REGISTRATIONS_CLOSED && (
              <Link to="/day-pass"><Button variant="outline">Back to day passes</Button></Link>
            )}
          </CardContent></Card>
        )}

        {/* PAID — the only state that may tell the buyer they're good to go. */}
        {view && view.status === 'paid' && (
          <Card>
            <CardContent className="py-6">
              <p className="text-white/90 mb-4 text-center">
                You&apos;re all set. At the entrance, give your name and staff will look you up and check you in.
                There&apos;s nothing to print.
              </p>
              {passList}
              {orderCodeNote}
            </CardContent>
          </Card>
        )}

        {/* PENDING — payment taken but not yet confirmed. Never promise the door. */}
        {view && view.status === 'pending' && (
          <Card>
            <CardContent className="py-6 space-y-4">
              <p className="text-orange-100 text-center">
                {loading
                  ? "Payment received. We're confirming it now. This page updates automatically."
                  : "Payment received. We're still confirming it with our payment provider. This usually takes a moment."}
              </p>
              {!loading && (
                <div className="text-center space-y-3">
                  <Button variant="outline" onClick={() => window.location.reload()}>
                    Check again
                  </Button>
                  <p className="text-white/80 text-sm">
                    Your passes below become valid as soon as payment confirms, and we&apos;ll email you then.
                    {view.code && (
                      <>
                        {' '}If you need help, quote order code{' '}
                        <span className="font-mono font-semibold text-white">{view.code}</span>.
                      </>
                    )}
                  </p>
                </div>
              )}
              {passList}
            </CardContent>
          </Card>
        )}

        {/* EXPIRED / CANCELLED — no payment was taken. Say so plainly. */}
        {view && (view.status === 'expired' || view.status === 'cancelled') && (
          <Card>
            <CardContent className="py-8 text-center space-y-4">
              <p className="text-white/90">
                This checkout {view.status === 'expired' ? 'expired' : 'was cancelled'}, so no payment was taken
                and no passes were issued.
              </p>
              {!REGISTRATIONS_CLOSED && (
                <Link to="/day-pass"><Button variant="outline">Buy day passes</Button></Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
